import ExcelJS from 'exceljs';

// Unwrap formula / rich-text / hyperlink cells to their plain value, and convert date cells back
// to the raw Excel serial number that XLSX.utils.sheet_to_json returned (exceljs parses date
// cells into JS Dates instead). Callers that need real Date instances rather than serial numbers
// (i.e. code that previously read with the `cellDates: true` xlsx option) should not use this --
// see cellToPrimitiveKeepDates below.
export const cellToPrimitive = (value: ExcelJS.CellValue): unknown => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (value instanceof Date) {
    return 25569 + value.getTime() / (24 * 60 * 60 * 1000);
  }
  if (typeof value === 'object') {
    if ('result' in value) {
      return cellToPrimitive(value.result);
    }
    if ('richText' in value) {
      return value.richText.map(run => run.text).join('');
    }
    if ('text' in value) {
      return value.text;
    }
    if ('error' in value) {
      return undefined;
    }
  }
  return value;
};

// Same cell unwrapping as cellToPrimitive, but keeps Date cells as real Date instances rather than
// converting them to Excel serial numbers -- for callers that read with xlsx's `cellDates: true`
// option previously.
export const cellToPrimitiveKeepDates = (value: ExcelJS.CellValue): unknown => {
  if (value instanceof Date) {
    return value;
  }
  return cellToPrimitive(value);
};

// Reproduces XLSX.utils.sheet_to_json's default behavior: row 1 holds the header names, each
// subsequent non-empty row becomes an object keyed by header, and empty cells are omitted.
export const sheetToJson = <T = Record<string, unknown>>(
  worksheet: ExcelJS.Worksheet | undefined,
  cellReader: (value: ExcelJS.CellValue) => unknown = cellToPrimitive
): T[] => {
  const rows: Record<string, unknown>[] = [];
  if (!worksheet) {
    return rows as T[];
  }

  const headers: Record<number, string> = {};
  worksheet.getRow(1).eachCell((cell, colNumber) => {
    const header = cellReader(cell.value);
    if (header != null && String(header).trim() !== '') {
      headers[colNumber] = String(header).trim();
    }
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) {
      return;
    }
    const rowObject: Record<string, unknown> = {};
    Object.entries(headers).forEach(([colNumber, header]) => {
      const value = cellReader(row.getCell(Number(colNumber)).value);
      if (value != null && value !== '') {
        rowObject[header] = value;
      }
    });
    if (Object.keys(rowObject).length > 0) {
      rows.push(rowObject);
    }
  });

  return rows as T[];
};

// Reproduces round-tripping an array of uniformly-shaped objects through
// XLSX.utils.json_to_sheet(data) then XLSX.utils.sheet_to_json(sheet, { header: 1 }): a header row
// (the first object's keys) followed by one array per object, values in that same key order.
export const jsonToAoa = (data: Record<string, unknown>[]): unknown[][] => {
  if (data.length === 0) {
    return [];
  }
  const headers = Object.keys(data[0]);
  return [headers, ...data.map(row => headers.map(header => row[header]))];
};

// Converts a spreadsheet column letter ('A', 'G', 'AA', ...) to its 1-indexed column number.
const columnLetterToNumber = (letters: string): number =>
  letters
    .toUpperCase()
    .split('')
    .reduce((acc, char) => acc * 26 + (char.charCodeAt(0) - 64), 0);

// Reproduces XLSX.utils.sheet_add_aoa(worksheet, aoa, { origin }): pastes a 2D array into the
// worksheet with its top-left cell at `origin` (e.g. 'G1'), leaving any existing content in other
// cells untouched.
export const pasteAoaAtOrigin = (
  worksheet: ExcelJS.Worksheet,
  aoa: unknown[][],
  origin: string
): void => {
  const match = origin.match(/^([A-Za-z]+)(\d+)$/);
  if (!match) {
    throw new Error(`Invalid cell origin: "${origin}"`);
  }
  const originCol = columnLetterToNumber(match[1]);
  const originRow = Number(match[2]);
  /* eslint-disable no-param-reassign -- mutating the worksheet is exceljs's whole API surface */
  aoa.forEach((rowValues, rowOffset) => {
    rowValues.forEach((value, colOffset) => {
      if (value !== undefined) {
        worksheet.getCell(originRow + rowOffset, originCol + colOffset).value =
          value as ExcelJS.CellValue;
      }
    });
  });
  /* eslint-enable no-param-reassign */
};
